import { useInCreateDialog } from 'components/create-dialog/CreateDialogContext';
import { CreateDialogFieldCompanion } from 'components/create-dialog/CreateDialogRequired';
import CheckboxInput from 'components/forms/inputs/rhf-inputs/CheckboxInput';
import DateInput from 'components/forms/inputs/rhf-inputs/DateInput';
import SelectInput from 'components/forms/inputs/rhf-inputs/SelectInput';
import TextAreaInput from 'components/forms/inputs/rhf-inputs/TextAreaInput';
import TextInput from 'components/forms/inputs/rhf-inputs/TextInput';
import { useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import { Link as RouterLink } from 'react-router-dom';
import { useLazyGetMinContactsListWithFiltersQuery } from 'store/api/contacts';
import { useLazyGetContractsListQuery } from 'store/api/contracts';
import { useLazyGetMinOfficesWithFiltersQuery } from 'store/api/offices';
import { ERROR_CODE_MESSAGES } from 'store/api/portalAPI';
import {
    useLazyGetActivePracticeAreasWithFiltersQuery,
    useLazyGetSecondLevelPracticeAreasUngroupedQuery,
} from 'store/api/practice-areas';
import { useLazyGetProjectsWithFiltersQuery } from 'store/api/projects';
import { RelationshipTypeCode } from 'types/contacts';
import { MatterStateType, PricingLimitationType } from 'types/matters';
import { cn } from 'utils/cn';
import { formatDateStringInput } from 'utils/formatDateStringInput';
import { getValidOptionsFromClassifier } from 'utils/formUtil';
import { getClassifierText, t } from 'utils/getTranslations';
import { officeInactiveSuffix } from 'utils/officeOptionRenderer';
import { formatPracticeAreaLabel } from 'utils/practiceAreaUtil';
import { ClientRiskProfileChip } from '../ClientRiskProfileChip';
import { EstimatedEngagementLengthInput } from '../EstimatedEngagementLengthInput';
import { OfferValueInput } from '../OfferValueInput';
import { OpenInNew as OpenInNewIcon } from 'components/icons';
import StatusChip from 'components/@extended/StatusChip.tsx';
import FeatureFlagged from 'components/featureflag/FeatureFlagged';
import ContactPickerModal from 'components/forms/form-components/select-picker/ContactPickerModal.tsx';
import DynamicMultiSelectInput from 'components/forms/inputs/rhf-inputs/DynamicMultiSelectInput';
import DynamicSelectInput from 'components/forms/inputs/rhf-inputs/DynamicSelectInput';
import { PaginatedListProps } from 'components/forms/inputs/rhf-inputs/usePaginatedList';
import InfoTooltip from 'components/InfoTooltip';
import ContactDynamicSelectPicker from 'components/party/ContactDynamicSelectPicker';
import { useInRecordSection, useSectionWideClass } from 'components/section-edit/SectionEdit';
import { DelegationAlert } from 'pages/delegations/DelegationAlert';
import { getContractStatusChipType } from 'pages/contracts/util.tsx';
import { ContractStatusType, ContractType } from 'types/contracts.ts';
import { FeatureFlag } from 'types/features';
import { PracticeArea, PracticeAreaType } from 'types/practice-areas';
import { useIsFeatureEnabled } from 'utils/useFeatureFlags.ts';
import {
    isConfidentialReasonFieldRequired,
    isConfidentialReasonFieldShown,
} from '../closing/confidentialReasonFormRules';
import { AmlMark } from '../components/AmlMark';
import { SinglePracticeAreaInput } from './SinglePracticeAreaInput';
import { MatterFormState, isClosingStatus, isOfferOutcomeStatus } from './useMatterForm';

function amlMark(area: PracticeArea) {
    return area.isAmlApplicable ? <AmlMark /> : null;
}

const PRACTICE_AREA_OPTIONS: PaginatedListProps<PracticeArea> = {
    fetch: useLazyGetActivePracticeAreasWithFiltersQuery,
    filters: `practiceAreaTypeCode==${PracticeAreaType.PRACTICE_AREA};showInCases==true`,
    searchField: 'name',
};

/** The case's own fields: name, client, status, office, practice areas, dates and the rest of the form's General section. */
export const CaseDetailsFields = ({ form }: { form: MatterFormState }): JSX.Element => {
    const { control, setValue, getValues, trigger } = form.methods;
    const {
        allowedRelationshipTypes,
        canReadInsiderInfo,
        canUpdateInsiderInfo,
        classifiers,
        clientParty,
        clientPartyFilter,
        clientPartyFullData,
        currentMatterStatus,
        formatServiceAgreementLabel,
        handleDmsPracticeAreaSelection,
        handlePracticeAreaChange,
        handlePracticeAreaSelection,
        handleSecondLevelPracticeAreaChange,
        handleSecondLevelPracticeAreaSelection,
        hasConnectedInvoices,
        hasContractSelectionChangedRef,
        isAdministrativeMatter,
        isChangingToOfferWon,
        isConfidential,
        isDMSPracticeAreaMandatory,
        isLinkDraftContractsEnabled,
        isLoadingNewClientData,
        isMatterTypeRequired,
        isNewPersonaNonGrataClient,
        isOfferFlowEnabled,
        isOneDriveIntegrationEnabled,
        isOpenedFromOfferWonCase,
        isPracticeAreaMandatoryOnCase,
        isRemunerationFieldEditable,
        isServiceAgreementRequired,
        isSubPracticeAreaEnabled,
        isTimecardIncrementControlEnabled,
        isTimecardIncrementTouchedRef,
        isUserPrivilegedToUpdateDmsLink,
        isUserPrivilegedToUpdateFirmWideAccess,
        matter,
        matterStateType,
        matterTypeOptions,
        practiceAreaForDMSOptions,
        prefillDescriptionOnFieldChange,
        prefillInvoicedByOffice,
        prefillTemplates,
        requiresClosedDate,
        resetClientPartyDependentFields,
        secondLevelPracticeAreasFilter,
        secondLevelPracticeAreasNarrowing,
        serviceAgreementContract,
        shouldShowRemunerationField,
        showMatterType,
        showOfferEstimateFields,
        statusOptions,
        timecardIncrementOptions,
        type,
        validateOpenedDate,
        isDraft,
    } = form;
    const inCreateDialog = useInCreateDialog();
    // On the record page each field is a cell of the Overview card's grid, where the read view has its row. The
    // case number and the alerts the page already shows above its cards are left to the page there.
    const inSection = useInRecordSection();
    const isSinglePracticeArea = useIsFeatureEnabled(FeatureFlag.CASE_PA_FIELD_SINGLE_SELECTION_ONLY);
    const serviceAgreementSpan = useSectionWideClass(control, 'serviceAgreementContract');
    const secondLevelPracticeAreaOptions = useMemo<PaginatedListProps<PracticeArea>>(
        () => ({
            fetch: useLazyGetSecondLevelPracticeAreasUngroupedQuery,
            searchField: 'name',
            filters: secondLevelPracticeAreasFilter,
            sort: ['parentPracticeArea.name,asc', 'name,asc'],
            ...secondLevelPracticeAreasNarrowing,
        }),
        [secondLevelPracticeAreasFilter, secondLevelPracticeAreasNarrowing]
    );
    const groupSubAreasByArea = (option: PracticeArea) =>
        option.parentPracticeArea ? formatPracticeAreaLabel(option.parentPracticeArea) : null;

    const serviceAgreementLink =
        serviceAgreementContract?.id != null ? (
            <RouterLink
                className="text-link inline-flex w-fit items-center gap-1 self-end hover:underline"
                to={`/contract/${serviceAgreementContract.id}`}
                target="_blank"
                rel="noopener noreferrer"
            >
                <FormattedMessage id="field.view" />
                <OpenInNewIcon fontSize="small" />
            </RouterLink>
        ) : null;

    return (
        <>
            {(type === 'edit' && !inSection) || (isDraft && !inCreateDialog) ? (
                <TextInput
                    name="readOnlyCaseNumber"
                    control={control}
                    label="matters.case-number"
                    placeholder="matters.case-number"
                    readonly
                    // A read-only field draws no placeholder: a draft, which has no number yet, says when it gets one.
                    readOnlyField={
                        isDraft ? (
                            <span className="text-fg-secondary text-sm">
                                <FormattedMessage id="matters.case-number-on-create" />
                            </span>
                        ) : undefined
                    }
                />
            ) : null}
            <TextInput
                name="name"
                control={control}
                label="matters.name"
                placeholder="matters.name"
                required
                onBlur={(val) => {
                    prefillDescriptionOnFieldChange(val, 'caseName');
                }}
            />
            <ContactDynamicSelectPicker
                setValue={setValue}
                control={control}
                label="matters.connected-client"
                placeholder="matters.select-client"
                name="clientParty"
                paginated={{
                    fetch: useLazyGetMinContactsListWithFiltersQuery,
                    searchField: ['name', 'partyNumber'],
                    filters: clientPartyFilter,
                }}
                isPickerModalOptionEnabled={(option) => allowedRelationshipTypes.includes(option.relationshipTypeCode)}
                onChange={(val) => {
                    resetClientPartyDependentFields();
                    prefillDescriptionOnFieldChange(val, 'clientName');

                    // If LEAD is selected, set state to OFFER_PENDING
                    if (val?.relationshipTypeCode === RelationshipTypeCode.LEAD) {
                        const offerPendingOption = (
                            getValidOptionsFromClassifier(classifiers['MATTER_STATE_TYPE']) as {
                                label: string;
                                value: string;
                            }[]
                        ).find((option) => option.value === MatterStateType.OFFER_PENDING);

                        if (offerPendingOption) {
                            setValue('matterStateType', offerPendingOption, { shouldDirty: true });
                            setValue('closedDate', null);
                        }
                    }

                    // If CLIENT is selected, set state to IN_PROGRESS
                    if (val?.relationshipTypeCode === RelationshipTypeCode.CLIENT) {
                        const inProgressOption = (
                            getValidOptionsFromClassifier(classifiers['MATTER_STATE_TYPE']) as {
                                label: string;
                                value: string;
                            }[]
                        ).find((option) => option.value === MatterStateType.IN_PROGRESS);

                        if (inProgressOption) {
                            setValue('matterStateType', inProgressOption, { shouldDirty: true });
                            setValue('closedDate', null);
                        }
                    }
                }}
                required
                disabled={type === 'edit' && hasConnectedInvoices}
                PickerModalComponent={ContactPickerModal}
                loading={isLoadingNewClientData}
                getSelectedValueEndAdornment={(option) =>
                    clientPartyFullData?.id === option.id ? (
                        <ClientRiskProfileChip riskProfileTypeCode={clientPartyFullData.riskProfileTypeCode} />
                    ) : null
                }
            />
            {isNewPersonaNonGrataClient ? (
                // The create dialog draws the client among its required fields, and the banner under it there.
                <CreateDialogFieldCompanion key="persona-non-grata-info" field="clientParty">
                    <DelegationAlert severity="blocked">
                        <FormattedMessage id="matters.tooltips.persona-non-grata" />
                    </DelegationAlert>
                </CreateDialogFieldCompanion>
            ) : null}
            {!inSection &&
            isOfferFlowEnabled &&
            isOfferOutcomeStatus(currentMatterStatus) &&
            matter?.hasUninvoicedBillableWork ? (
                <DelegationAlert key="uninvoiced-billable-warning" severity="warning">
                    <FormattedMessage id="matters.uninvoiced-billable-warning" />
                </DelegationAlert>
            ) : null}
            {!inSection && matter?.isCurrentlyBlocked ? (
                <DelegationAlert key="matter-timecard-blocked-info" severity="blocked">
                    <FormattedMessage id="dashboard.tooltips.timecard-blocked" />
                </DelegationAlert>
            ) : null}
            {showMatterType ? (
                <SelectInput
                    setValue={setValue}
                    control={control}
                    label="matters.case-type"
                    placeholder="matters.select-case-type"
                    name="matterType"
                    options={matterTypeOptions}
                    getOptionLabel="label"
                    getOptionIdentifier="value"
                    required={isMatterTypeRequired}
                />
            ) : null}
            <SelectInput
                setValue={setValue}
                control={control}
                label="matters.state"
                placeholder="matters.select-state"
                name="matterStateType"
                options={statusOptions}
                getOptionDisabled={(option) => {
                    // LEAD clients cannot have IN_PROGRESS or COMPLETED status, except on a case
                    // opened from an Offer Won case: saving it promotes the Lead to a Client.
                    if (
                        !isOpenedFromOfferWonCase &&
                        clientPartyFullData?.relationshipTypeCode === RelationshipTypeCode.LEAD
                    ) {
                        if (
                            option.value === MatterStateType.IN_PROGRESS ||
                            option.value === MatterStateType.COMPLETED
                        ) {
                            return true;
                        }
                    }
                    // For edit mode, check if user has permission for this status transition
                    if (type === 'edit' && matter?.allowedStatusTransitions) {
                        return (
                            option.value !== currentMatterStatus &&
                            !matter.allowedStatusTransitions.includes(option.value)
                        );
                    }
                    return false;
                }}
                getOptionDescription={(option) => {
                    const blockerCode = matter?.statusTransitionBlockers?.[option.value];
                    if (!blockerCode) return '';
                    const id = ERROR_CODE_MESSAGES.get(blockerCode);
                    return id ? t(`error-code-message.${id}`) : '';
                }}
                onChange={(matterState) => {
                    const isClosing = isOfferFlowEnabled
                        ? isClosingStatus(matterState?.value)
                        : matterState?.value === MatterStateType.COMPLETED;
                    if (isClosing) {
                        // Event-handler only, never rendered — no SSR/hydration in this SPA.
                        // oxlint-disable-next-line react-doctor/rendering-hydration-mismatch-time
                        setValue('closedDate', formatDateStringInput(new Date()));
                    } else if (matterState) {
                        setValue('closedDate', null);
                    }

                    if (matterState?.value !== MatterStateType.OFFER_LOST) {
                        setValue('reasonOfferLost', null);
                    }

                    if (!isOfferOutcomeStatus(matterState?.value)) {
                        setValue('outcomeNotes', '');
                    }

                    if (type === 'add' && matterState?.value === MatterStateType.OFFER_PENDING) {
                        const nonBillableOption = (
                            getValidOptionsFromClassifier(classifiers['PRICING_LIMITATION_TYPE']) as {
                                label: string;
                                value: string;
                            }[]
                        ).find((option) => option.value === PricingLimitationType.NON_BILLABLE);

                        if (nonBillableOption) {
                            setValue('pricingLimitationType', nonBillableOption, {
                                shouldDirty: true,
                            });
                        }

                        setValue('preserveNonBillableHours', true, { shouldDirty: true });
                    }
                }}
                getOptionLabel="label"
                getOptionIdentifier="value"
                required
            />
            {isChangingToOfferWon ? (
                <p className="text-fg-secondary mt-[3px] ml-3.5 text-xs">
                    <FormattedMessage id="matters.status-changing-to-offer-won" />
                </p>
            ) : null}
            {isOfferFlowEnabled && matterStateType?.value === MatterStateType.OFFER_LOST ? (
                <SelectInput
                    setValue={setValue}
                    control={control}
                    label="matters.reason-offer-lost"
                    placeholder="matters.select-reason"
                    name="reasonOfferLost"
                    tooltip={<InfoTooltip text="matters.reason-offer-lost-tooltip" />}
                    options={
                        (getValidOptionsFromClassifier(classifiers['REASON_OFFER_LOST']) as {
                            label: string;
                            value: string;
                        }[]) ?? []
                    }
                    getOptionLabel="label"
                    getOptionIdentifier="value"
                    noPreSelect
                    required
                />
            ) : null}
            {isOfferFlowEnabled && isOfferOutcomeStatus(matterStateType?.value) ? (
                <TextAreaInput
                    control={control}
                    label="matters.outcome-notes"
                    placeholder="matters.outcome-notes-placeholder"
                    name="outcomeNotes"
                    tooltip={<InfoTooltip text="matters.outcome-notes-tooltip" />}
                    enableAutofill
                />
            ) : null}
            {showOfferEstimateFields ? (
                <>
                    <OfferValueInput
                        control={control}
                        setValue={setValue}
                        getValues={getValues}
                        trigger={trigger}
                        classifiers={classifiers}
                    />
                    <EstimatedEngagementLengthInput control={control} setValue={setValue} classifiers={classifiers} />
                </>
            ) : null}
            <DynamicSelectInput
                setValue={setValue}
                control={control}
                label="matters.handled-by-office"
                placeholder="matters.select-office"
                name="handledByOffice"
                paginated={{
                    fetch: useLazyGetMinOfficesWithFiltersQuery,
                    searchField: 'name',
                    filters: 'isActive!=false',
                }}
                getOptionLabel="name"
                getOptionIdentifier="id"
                getOptionEndAdornment={officeInactiveSuffix}
                onChange={(val) => {
                    prefillInvoicedByOffice(val);
                    prefillTemplates(val);
                }}
                required={!isAdministrativeMatter}
            />
            <TextAreaInput
                control={control}
                label="matters.description"
                placeholder="matters.description"
                name="description"
                required
            />
            {shouldShowRemunerationField && (
                <FeatureFlagged action="show" flag={FeatureFlag.REMUNERATION_CALC_TYPE_CONTROL}>
                    <CheckboxInput
                        control={control}
                        name="isUsedTimeUsedForRemuneration"
                        label="matters.use-used-time-for-remuneration"
                        disabled={!isRemunerationFieldEditable}
                    />
                </FeatureFlagged>
            )}
            <CheckboxInput
                control={control}
                name="isConfidential"
                label="matters.is-confidential"
                onChange={(checked) => {
                    if (!checked) {
                        setValue('confidentialReason', '');
                        setValue('isInsider', false);
                    }
                }}
            />
            {isConfidential && canReadInsiderInfo && canUpdateInsiderInfo && (
                <FeatureFlagged action="show" flag={FeatureFlag.MATTER_INSIDER_LIST}>
                    <CheckboxInput control={control} name="isInsider" label="matters.is-insider" />
                </FeatureFlagged>
            )}
            {type === 'edit' && isConfidentialReasonFieldShown(isConfidential, matterStateType?.value) ? (
                <TextAreaInput
                    control={control}
                    label="case-closing-confidentiality.reason-label"
                    placeholder="case-closing-confidentiality.reason-label"
                    name="confidentialReason"
                    required={isConfidentialReasonFieldRequired(
                        isConfidential,
                        matterStateType?.value,
                        matter?.matterStateTypeCode,
                        matter?.isConfidentialReasonRequiredOnClose === true
                    )}
                />
            ) : null}
            {isConfidential && canReadInsiderInfo && canUpdateInsiderInfo && (
                <FeatureFlagged action="show" flag={FeatureFlag.MATTER_INSIDER_LIST}>
                    <TextAreaInput
                        control={control}
                        label="matters.insider-info"
                        placeholder="matters.insider-info"
                        name="insiderInfo"
                    />
                </FeatureFlagged>
            )}
            {clientPartyFullData?.isInternalClient ? (
                <CheckboxInput
                    control={control}
                    name="isAccessibleFirmWide"
                    label="matters.enable-firm-wide-access"
                    rightLabelComponent={<InfoTooltip text={'matters.enable-firm-wide-access-tooltip'} />}
                    disabled={!isUserPrivilegedToUpdateFirmWideAccess || isLoadingNewClientData}
                />
            ) : null}
            {isSinglePracticeArea ? (
                <SinglePracticeAreaInput
                    control={control}
                    required={isPracticeAreaMandatoryOnCase}
                    paginated={PRACTICE_AREA_OPTIONS}
                    onPracticeAreaChange={handlePracticeAreaChange}
                    onPracticeAreaSelect={handlePracticeAreaSelection}
                    getSelectedValueEndAdornment={inSection ? amlMark : undefined}
                />
            ) : (
                <DynamicMultiSelectInput
                    setValue={setValue}
                    control={control}
                    label="matters.practice-areas"
                    placeholder="matters.select-practice-areas"
                    name="practiceAreas"
                    getOptionLabel={(option) => formatPracticeAreaLabel(option)}
                    getOptionIdentifier="id"
                    required={isPracticeAreaMandatoryOnCase}
                    paginated={PRACTICE_AREA_OPTIONS}
                    onChange={(_, reason, details) => {
                        if (reason === 'selectOption' && details?.option) {
                            handlePracticeAreaSelection(details.option);
                        } else if (reason === 'clear' || reason === 'removeOption') {
                            handlePracticeAreaChange(details?.option || null, reason);
                        }
                    }}
                    getSelectedValueEndAdornment={inSection ? amlMark : undefined}
                />
            )}
            <FeatureFlagged action="show" flag={FeatureFlag.SUB_PRACTICE_AREA}>
                <DynamicMultiSelectInput
                    setValue={setValue}
                    control={control}
                    label="matters.second-level-practice-areas"
                    placeholder="matters.select-practice-areas"
                    name="secondLevelPracticeAreas"
                    getOptionLabel={(option) => formatPracticeAreaLabel(option)}
                    getOptionIdentifier="id"
                    groupByField="parentPracticeArea.name"
                    groupByStaticFields={groupSubAreasByArea}
                    paginated={secondLevelPracticeAreaOptions}
                    onChange={(_newValue, reason, details) => {
                        if (reason === 'selectOption' && details?.option) {
                            handleSecondLevelPracticeAreaSelection(details.option);
                        } else if (reason === 'clear' || reason === 'removeOption') {
                            handleSecondLevelPracticeAreaChange(details?.option || null, reason);
                        }
                    }}
                    getSelectedValueEndAdornment={inSection ? amlMark : undefined}
                />
            </FeatureFlagged>
            {!isOneDriveIntegrationEnabled &&
                (practiceAreaForDMSOptions.length > 0 ? (
                    <SelectInput
                        control={control}
                        setValue={setValue}
                        label="matters.practice-area-for-dms"
                        placeholder="matters.select-practice-area-for-dms"
                        tooltip={<InfoTooltip text={'matters.tooltips.practice-area-for-dms'} />}
                        name="dmsPracticeArea"
                        options={practiceAreaForDMSOptions}
                        getOptionIdentifier="id"
                        getOptionLabel={(option) => formatPracticeAreaLabel(option)}
                        required={isDMSPracticeAreaMandatory}
                        // Opening a case leaves an empty DMS area empty, even with one area offered.
                        noPreSelect
                    />
                ) : (
                    // With none of the areas it offers on the case yet, the field lists every one the case can
                    // take, and its pick puts that area on the case as well.
                    <DynamicSelectInput
                        control={control}
                        setValue={setValue}
                        label="matters.practice-area-for-dms"
                        placeholder="matters.select-practice-area-for-dms"
                        tooltip={<InfoTooltip text={'matters.tooltips.practice-area-for-dms'} />}
                        name="dmsPracticeArea"
                        getOptionIdentifier="id"
                        getOptionLabel={(option) => formatPracticeAreaLabel(option)}
                        required={isDMSPracticeAreaMandatory}
                        groupByStaticFields={isSubPracticeAreaEnabled ? groupSubAreasByArea : undefined}
                        paginated={isSubPracticeAreaEnabled ? secondLevelPracticeAreaOptions : PRACTICE_AREA_OPTIONS}
                        onChange={handleDmsPracticeAreaSelection}
                    />
                ))}
            {/* On the record page this is the agreement's cell, with its read row's span, and the View link takes
                the line the read view's price list has under the agreement. */}
            <div className={cn('flex flex-col gap-2', inSection && 'form-field leading-5', serviceAgreementSpan)}>
                <DynamicSelectInput
                    setValue={setValue}
                    control={control}
                    label="matters.service-agreement"
                    placeholder="matters.select-service-agreement"
                    name="serviceAgreementContract"
                    required={isServiceAgreementRequired}
                    // The create dialog draws a required field apart from its section: there the link is the
                    // field's own, so it goes where the field goes.
                    bottomRightLabelComponent={inCreateDialog ? serviceAgreementLink : undefined}
                    onChange={() => {
                        hasContractSelectionChangedRef.current = true;
                    }}
                    paginated={{
                        fetch: useLazyGetContractsListQuery,
                        filters: `contractStatusTypeCode${isLinkDraftContractsEnabled ? `=in=(${ContractStatusType.DRAFT},${ContractStatusType.VALID})` : `==${ContractStatusType.VALID}`};contractTypeCode==${ContractType.SERVICE_AGREEMENT};parties.party.id=in=(${clientParty?.id ?? -1})`,
                        isEnabled: clientParty?.id !== null,
                        searchField: 'contractName',
                    }}
                    getOptionLabel={formatServiceAgreementLabel}
                    getOptionIdentifier="id"
                    getOptionEndAdornment={(option) => {
                        return (
                            <div className="flex flex-row items-center gap-1">
                                <StatusChip
                                    type={getContractStatusChipType(option.contractStatusTypeCode)}
                                    label={getClassifierText(
                                        classifiers['CONTRACT_STATUS_TYPE'],
                                        option.contractStatusTypeCode
                                    )}
                                />
                                {option.isClientTemplate && (
                                    <StatusChip
                                        type="neutral"
                                        label={<FormattedMessage id="contracts.client-template" />}
                                    />
                                )}
                            </div>
                        );
                    }}
                    getOptionAction={(option) => (
                        <RouterLink
                            className="text-link inline-flex items-center whitespace-nowrap hover:underline"
                            to={`/contract/${option.id}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            aria-label={t('contracts.view-contract-form')}
                            title={t('contracts.view-contract-form')}
                        >
                            <OpenInNewIcon fontSize="small" />
                        </RouterLink>
                    )}
                />
                {inCreateDialog ? null : serviceAgreementLink}
            </div>
            {isTimecardIncrementControlEnabled ? (
                <SelectInput
                    setValue={setValue}
                    control={control}
                    label="matters.timecard-increment"
                    placeholder="matters.select-timecard-increment"
                    name="timecardIncrement"
                    options={timecardIncrementOptions}
                    getOptionLabel="label"
                    getOptionIdentifier="value"
                    noPreSelect
                    onChange={() => {
                        isTimecardIncrementTouchedRef.current = true;
                    }}
                    required
                />
            ) : null}
            <DynamicSelectInput
                setValue={setValue}
                control={control}
                label="projects.projects"
                placeholder="matters.select-projects"
                name="project"
                paginated={{
                    fetch: useLazyGetProjectsWithFiltersQuery,
                    searchField: 'projectName',
                }}
                getOptionLabel="projectName"
                getOptionIdentifier="id"
            />
            <div className={inSection ? 'contents' : 'grid grid-cols-1 gap-4 sm:grid-cols-2'}>
                <DateInput
                    fullWidth
                    control={control}
                    name="openedDate"
                    required
                    label="matters.opening-date"
                    validate={validateOpenedDate}
                />
                <DateInput
                    disabled={!requiresClosedDate}
                    control={control}
                    name="closedDate"
                    required={requiresClosedDate}
                    label="matters.closing-date"
                    fullWidth
                />
            </div>
            <TextAreaInput
                control={control}
                label="matters.general-notes"
                placeholder="matters.general-notes"
                name="generalNotes"
                enableAutofill
            />
            <FeatureFlagged action="show" flag={FeatureFlag.CASE_OFFICIAL_REF_NUMBER_FIELD}>
                <TextInput
                    name="officialReference"
                    control={control}
                    label="matters.official-reference-number"
                    rightLabelComponent={<InfoTooltip text="matters.tooltips.official-reference-number" />}
                />
            </FeatureFlagged>
            <TextInput
                name="dmsLink"
                control={control}
                label="matters.dms-link"
                disabled={!isUserPrivilegedToUpdateDmsLink}
            />
        </>
    );
};
